import { useState, useEffect, useRef } from "react";
import { apiFetch, can, getPermissions } from "../api";
import { LinkedClaimsList } from "./LinkedLists";
import EditCompanyModal from "./EditCompanyModal";  // mutual import: only used in render, cycle resolves fine

// ============================================================================
// EditContactModal (Chunk 7F) — shared between the Contacts page and the
// Admin Review Queue. Mirrors EditCompanyModal: per-field amber highlighting
// when current values differ from last_reviewed_snapshot, admin-only
// "Approve as Reviewed" action on pending rows.
//
// Company linkage uses a debounced typeahead against GET /companies?search=…
// instead of a full dropdown because we expect 500+ companies.
//
// Props:
//   contact   — contact row (must include last_reviewed_snapshot, reviewed_at,
//               company_id and optionally company_name for display).
//               Pass null/undefined for CREATE mode (CONTACT-CREATE-1).
//   isAdmin   — whether to show the Approve action
//   onClose() — close without reloading
//   onSaved() — close and caller should reload their list (edit mode)
//
// Create-mode props (CONTACT-CREATE-1, 2026-07-05 — Directory "+ New Contact",
// Parties picker "+ New contact…", Company modal "+ Add contact"):
//   fixedCompanyId / fixedCompanyName — prefill AND lock the company field
//   onCreated(row) — called with the created contact row after POST /contacts
// ============================================================================

// Fields tracked in the review snapshot. Must stay in sync with
// _refresh_contact_review() in main.py (CONTACT_SNAPSHOT_FIELDS).
const REVIEWABLE_FIELDS = [
  "first_name", "last_name", "email", "title",
  "phone", "phone_mobile", "fax", "company_id", "notes",
];

function fieldDiffers(snapshot, row, field) {
  if (!snapshot) return false;
  const s = snapshot[field]; const c = row[field];
  const norm = v => (v === null || v === undefined || v === "") ? null : v;
  return norm(s) !== norm(c);
}

function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// CompanyTypeahead — debounced search against GET /companies?search=…
// Keeps a local selectedLabel for display even if company_id points at a
// company we didn't load into the dropdown's result set.
// ----------------------------------------------------------------------------
function CompanyTypeahead({ value, initialLabel, onChange, pendingStyle }) {
  // value is a numeric company_id or null. initialLabel is the company_name
  // for the already-linked row, used when the component mounts so the user
  // sees "ACME Insurance" instead of "#12".
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(initialLabel || "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Debounced fetch; ignores empty queries to avoid flooding on mount.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      apiFetch(`/companies?search=${encodeURIComponent(query.trim())}&include_inactive=true`)
        .then(rows => { setResults(rows.slice(0, 20)); setLoading(false); })
        .catch(() => { setResults([]); setLoading(false); });
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open]);

  const pick = (row) => {
    setSelectedLabel(row.company_name);
    setQuery("");
    setResults([]);
    setOpen(false);
    onChange(row.id);
  };
  const clear = () => {
    setSelectedLabel("");
    setQuery("");
    setResults([]);
    setOpen(false);
    onChange(-1);   // -1 sentinel → backend clears company_id
  };

  const hasValue = value != null && value !== -1;
  const containerStyle = pendingStyle ? { ...pendingStyle, padding: 6, borderRadius: 4, border: "1px solid" } : undefined;

  return (
    <div style={containerStyle}>
      {hasValue && !open ? (
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{flex:1}}>{selectedLabel || `Company #${value}`}</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setOpen(true)}>Change</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={clear}>Clear</button>
        </div>
      ) : (
        <div style={{position:"relative"}}>
          <input
            autoFocus={open}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={hasValue ? `Currently: ${selectedLabel || `#${value}`} — type to search` : "Search companies..."}
            style={{width:"100%"}}
          />
          {(results.length > 0 || loading) && (
            <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--border)",borderRadius:4,marginTop:2,maxHeight:220,overflowY:"auto",zIndex:10,boxShadow:"0 2px 8px rgba(0,0,0,0.1)"}}>
              {loading && <div style={{padding:"8px 10px",fontSize:12,color:"var(--text3)"}}>Searching…</div>}
              {!loading && results.map(r => (
                <div key={r.id}
                  onClick={() => pick(r)}
                  style={{padding:"8px 10px",cursor:"pointer",fontSize:13,borderBottom:"1px solid var(--border)"}}
                  onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                  <div style={{fontWeight:500}}>{r.company_name}</div>
                  {(r.city || r.state) && <div style={{fontSize:11,color:"var(--text3)"}}>{[r.city, r.state].filter(Boolean).join(", ")}</div>}
                </div>
              ))}
              {!loading && query.trim() && results.length === 0 && (
                <div style={{padding:"8px 10px",fontSize:12,color:"var(--text3)"}}>No matches.</div>
              )}
            </div>
          )}
          {hasValue && open && (
            <div style={{marginTop:6}}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setOpen(false); setQuery(""); setResults([]); }}>Cancel</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={clear} style={{marginLeft:6}}>Unlink company</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EditContactModal({ contact, isAdmin, onClose, onSaved, onNavigateToClaim, fixedCompanyId, fixedCompanyName, onCreated }) {
  // CONTACT-CREATE-1: no contact row (or no id) = create mode. All the
  // review-snapshot machinery below is inert in create mode because `c`
  // has no snapshot/needs_admin_review fields.
  const isCreate = !contact || !contact.id;
  const c = contact || {};
  const [form, setForm] = useState({
    first_name: c.first_name || "",
    last_name: c.last_name || "",
    email: c.email || "",
    title: c.title || "",
    phone: c.phone || "",
    phone_mobile: c.phone_mobile || "",
    fax: c.fax || "",
    company_id: fixedCompanyId ?? c.company_id ?? null,
    notes: c.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Pop-out: open the linked company's modal in place (no page navigation).
  const [openCompany, setOpenCompany] = useState(null);
  const openLinkedCompany = async (id) => {
    try {
      const [co, sch] = await Promise.all([apiFetch(`/companies/${id}`), apiFetch(`/rate-schedules`)]);
      setOpenCompany({ co, sch });
    } catch (e) { setErr(parseErr(e, "Failed to open company")); }
  };

  const snapshot = c.last_reviewed_snapshot;
  const neverReviewed = c.needs_admin_review && !snapshot;
  const isPendingField = (field) => !isCreate && c.needs_admin_review && !neverReviewed && fieldDiffers(snapshot, c, field);
  const pendingStyle = { borderColor: "#f59e0b", background: "#fffbeb" };

  const displayName = [c.first_name, c.last_name].filter(Boolean).join(" ") || "(unnamed contact)";

  const save = async () => {
    setErr(null);
    if (!form.first_name.trim() && !form.last_name.trim()) { setErr("At least one of first or last name is required"); return; }
    setSaving(true);
    if (isCreate) {
      // CONTACT-CREATE-1: POST the full form; empty strings become null.
      try {
        const t = (v) => { const s = (v || "").trim(); return s || null; };
        const row = await apiFetch("/contacts", { method: "POST", body: {
          first_name: t(form.first_name),
          last_name: t(form.last_name),
          email: t(form.email),
          title: t(form.title),
          phone: t(form.phone),
          phone_mobile: t(form.phone_mobile),
          fax: t(form.fax),
          company_id: (form.company_id != null && form.company_id !== -1) ? Number(form.company_id) : null,
          notes: t(form.notes),
        }});
        if (onCreated) onCreated(row); else onSaved?.();
      } catch (e) { setErr(parseErr(e, "Failed to create contact")); setSaving(false); }
      return;
    }
    try {
      // Build patch: only include keys that actually changed vs. the original
      // contact. company_id uses the -1 sentinel when the user explicitly
      // cleared it; omission = no change.
      const patch = {};
      const textFields = ["first_name","last_name","email","title","phone","phone_mobile","fax","notes"];
      for (const f of textFields) {
        const cur = (form[f] || "");
        const orig = (contact[f] || "");
        if (cur.trim() !== orig) patch[f] = cur.trim() || null;
      }
      const origCompany = contact.company_id ?? null;
      const newCompany = form.company_id;
      if (newCompany !== origCompany) {
        if (newCompany === -1 || newCompany === null) {
          patch.company_id = -1;   // backend sentinel for "clear"
        } else {
          patch.company_id = Number(newCompany);
        }
      }
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await apiFetch(`/contacts/${contact.id}`, { method:"PATCH", body: patch });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to save")); setSaving(false); }
  };

  // LD-REV-006 / H18 — per-field Revert beside the amber tint.  See the twin
  // in EditCompanyModal.js.
  const revertField = async (field) => {
    const prior = snapshot ? snapshot[field] : null;
    const shown = (prior === null || prior === undefined || prior === "") ? "— empty —" : String(prior);
    if (!window.confirm(`Revert ${field.replace(/_/g, " ")} to the last approved value?\n\n${shown}`)) return;
    setErr(null); setSaving(true);
    try {
      await apiFetch(`/contacts/${contact.id}/revert-to-snapshot`, { method:"POST", body:{ fields:[field] } });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to revert")); setSaving(false); }
  };
  // LD-REV-043: the proposer may withdraw (revert) their own unapproved proposal.
  const mineField = (field) => ((contact.pending_proposer_by_field || {})[field] === getPermissions().user_id);
  const rev = (field) => (!isCreate && (isAdmin || mineField(field)) && isPendingField(field)) ? (
    <button type="button" className="btn btn-outline btn-sm" onClick={() => revertField(field)}
            disabled={saving} title="Restore the last approved value"
            style={{marginLeft:8, padding:"0 6px", fontSize:10, fontWeight:500, textTransform:"none", letterSpacing:"normal"}}>
      Revert
    </button>
  ) : null;

  const approve = async () => {
    if (!window.confirm(`Approve "${displayName}" as reviewed? This will snapshot the current values as the new baseline.`)) return;
    setSaving(true);
    try {
      await apiFetch(`/contacts/${contact.id}/approve`, { method:"POST" });
      onSaved();
    } catch (e) { setErr(parseErr(e, "Failed to approve")); setSaving(false); }
  };

  // Pop-out: the linked company was clicked — show its modal in place; closing
  // it returns here.
  if (openCompany) {
    // Swap (not stack): clicking through replaces this modal with the company's.
    // Close/Save closes the whole thing — no back-stepping through a stack.
    return <EditCompanyModal
      company={openCompany.co}
      schedules={openCompany.sch}
      isAdmin={can("approve_companies")}
      onClose={onClose}
      onSaved={onClose}
      onNavigateToClaim={onNavigateToClaim}
    />;
  }

  return (
    <Modal
      title={isCreate ? (fixedCompanyName ? `New Contact — ${fixedCompanyName}` : "New Contact") : `Edit Contact: ${displayName}`}
      onClose={onClose}
      footer={<>
        {!isCreate && isAdmin && c.needs_admin_review && (
          <button className="btn btn-primary btn-sm" onClick={approve} disabled={saving} style={{marginRight:"auto"}}>Approve as Reviewed</button>
        )}
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":(isCreate?"Create Contact":"Save")}</button>
      </>}
    >
      {err && <div className="alert-error">{err}</div>}

      {isCreate && !isAdmin && (
        <div style={{background:"#eff6ff",border:"1px solid #93c5fd",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#1e40af"}}>
          New contacts are submitted for admin review. You can still use this record immediately — an admin will verify the details shortly.
        </div>
      )}

      {neverReviewed && (
        <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#92400e"}}>
          <strong>Awaiting first admin review.</strong> This contact was imported from VCA or created by a non-admin and hasn't been approved yet. {isAdmin ? 'You can approve it with "Approve as Reviewed" below once the details look right.' : "An admin will review the details shortly."}
        </div>
      )}
      {!isCreate && c.needs_admin_review && !neverReviewed && (
        <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#92400e"}}>
          <strong>Pending admin review.</strong> Fields highlighted in amber differ from the last approved values. {isAdmin && 'Use "Approve as Reviewed" to accept the current values as the new baseline, or "Revert" beside a field to restore the approved value.'}
        </div>
      )}

      <div className="form-grid">
        <div className="form-group">
          <label>First Name{rev("first_name")}</label>
          <input value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} style={isPendingField("first_name") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Last Name{rev("last_name")}</label>
          <input value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} style={isPendingField("last_name") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group full">
          <label>Title{rev("title")}</label>
          <input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} style={isPendingField("title") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Email{rev("email")}</label>
          <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={isPendingField("email") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Phone{rev("phone")}</label>
          <input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} style={isPendingField("phone") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Mobile{rev("phone_mobile")}</label>
          <input value={form.phone_mobile} onChange={e=>setForm({...form,phone_mobile:e.target.value})} style={isPendingField("phone_mobile") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group">
          <label>Fax{rev("fax")}</label>
          <input value={form.fax} onChange={e=>setForm({...form,fax:e.target.value})} style={isPendingField("fax") ? pendingStyle : undefined}/>
        </div>
        <div className="form-group full">
          <label style={{display:"flex",alignItems:"center",gap:10}}>
            Company
            {rev("company_id")}
            {!isCreate && form.company_id != null && form.company_id !== -1 && (
              <button type="button" onClick={() => openLinkedCompany(Number(form.company_id))}
                style={{border:"none",background:"none",color:"var(--blue,#2E75B6)",cursor:"pointer",fontSize:12,fontWeight:600,padding:0,textTransform:"none",letterSpacing:"normal"}}>
                Open company →
              </button>
            )}
          </label>
          {fixedCompanyId != null ? (
            // CONTACT-CREATE-1: launched from a company-scoped surface — the
            // linkage is locked so the new contact lands under that company.
            <div style={{display:"flex",alignItems:"center",gap:8,padding:6,border:"1px solid var(--border)",borderRadius:4,background:"var(--bg,#F8F9FB)"}}>
              <span style={{flex:1,fontWeight:500}}>{fixedCompanyName || `Company #${fixedCompanyId}`}</span>
              <span style={{fontSize:11,color:"var(--text3)"}}>locked</span>
            </div>
          ) : (
            <CompanyTypeahead
              value={form.company_id}
              initialLabel={c.company_name || ""}
              onChange={v => setForm({...form, company_id: v})}
              pendingStyle={isPendingField("company_id") ? pendingStyle : null}
            />
          )}
          {fixedCompanyId == null && (form.company_id === null || form.company_id === -1) && (
            <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>No company linked — this contact is standalone.</div>
          )}
        </div>
        <div className="form-group full">
          <label>Notes{rev("notes")}</label>
          <textarea rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} style={isPendingField("notes") ? pendingStyle : undefined}/>
        </div>
      </div>

      {!isCreate && c.reviewed_at && (
        <div style={{marginTop:12,fontSize:11,color:"var(--text3)"}}>
          Last reviewed: {new Date(c.reviewed_at).toLocaleString()}
        </div>
      )}

      {/* Chunk 7G: linked claims where this contact appears in claim_parties. */}
      {!isCreate && <LinkedClaimsList
        url={`/contacts/${c.id}/claims`}
        rolesField="contact_roles"
        onNavigateToClaim={onNavigateToClaim ? (id) => { onClose?.(); onNavigateToClaim(id); } : undefined}
      />}
    </Modal>
  );
}
